import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import { Badge, Button, Card, ErrorBanner, FilterSelect, PageHeader, Pagination, SearchBox, Spinner, Table } from '../../components/ui';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { inr } from '../../lib/format';
import { useUrlState, type Paged } from '../../lib/hooks';

export interface Category { id: string; name: string; slug: string; parentId: string | null; status: string; displayOrder: number; productCount: number; description: string | null; imageUrl: string | null; seoTitle: string | null; seoDescription: string | null }
interface Row { id: string; name: string; slug: string; status: string; isFeatured: boolean; isBestSeller: boolean; image: string | null; category: { name: string }; variants: { id: string; sku: string; label: string; pricePaise: number; available: number; lowStock: boolean; status: string }[] }

export const useCategories = () => useQuery({ queryKey: ['categories'], queryFn: () => api<Category[]>('/admin/categories'), staleTime: 60_000 });
export function categoryOptions(cats: Category[]) {
  const byParent = new Map<string | null, Category[]>();
  for (const c of cats) byParent.set(c.parentId, [...(byParent.get(c.parentId) ?? []), c]);
  const out: { value: string; label: string }[] = [];
  const walk = (parent: string | null, depth: number) => (byParent.get(parent) ?? []).forEach((c) => { out.push({ value: c.id, label: `${'— '.repeat(depth)}${c.name}` }); walk(c.id, depth + 1); });
  walk(null, 0);
  return out;
}

const DEFAULTS = { q: '', status: '', categoryId: '', lowStock: '', page: '1' };

export function Products() {
  const nav = useNavigate();
  const { can } = useAuth();
  const cats = useCategories();
  const [f, setF] = useUrlState(DEFAULTS);
  const query = { ...f, pageSize: 25 };
  const q = useQuery({ queryKey: ['products', query], queryFn: () => api<Paged<Row>>('/admin/products', { query }), placeholderData: keepPreviousData });
  return (
    <>
      <PageHeader title="Products" actions={can('products.create') && <Link to="/products/new"><Button><Plus className="h-4 w-4" />New product</Button></Link>} />
      <Card>
        <div className="mb-3 flex flex-wrap gap-2">
          <SearchBox value={f.q} onChange={(v) => setF({ q: v })} placeholder="Name or SKU" />
          <FilterSelect label="Status" value={f.status} onChange={(v) => setF({ status: v })} options={[{ value: '', label: 'All statuses' }, ...['ACTIVE', 'DRAFT', 'INACTIVE', 'ARCHIVED'].map((s) => ({ value: s, label: s[0] + s.slice(1).toLowerCase() }))]} />
          <FilterSelect label="Category" value={f.categoryId} onChange={(v) => setF({ categoryId: v })} options={[{ value: '', label: 'All categories' }, ...categoryOptions(cats.data ?? [])]} />
          <Button variant={f.lowStock === 'true' ? 'primary' : 'secondary'} onClick={() => setF({ lowStock: f.lowStock === 'true' ? '' : 'true' })}>Low stock</Button>
        </div>
        {q.isLoading ? <Spinner /> : q.error ? <ErrorBanner error={q.error} /> : (
          <>
            <Table rows={q.data!.items} rowKey={(p) => p.id} onRowClick={(p) => nav(`/products/${p.id}`)} empty="No products yet." columns={[
              { header: '', cell: (p) => (p.image ? <img src={p.image} alt="" className="h-10 w-10 rounded object-cover" /> : <div className="h-10 w-10 rounded bg-cream" />), className: 'w-12' },
              { header: 'Product', cell: (p) => <div><div className="font-medium text-forest-800">{p.name}</div><div className="text-xs text-ink-muted">{p.category.name}{p.isFeatured && ' · Featured'}{p.isBestSeller && ' · Best seller'}</div></div> },
              { header: 'Variants', cell: (p) => <ul className="space-y-0.5 text-xs">{p.variants.map((v) => <li key={v.id} className="whitespace-nowrap">{v.label} · {inr(v.pricePaise)} · <span className={v.available <= 0 ? 'text-danger' : v.lowStock ? 'text-gold-700' : ''}>{v.available} in stock</span></li>)}</ul> },
              { header: 'Status', cell: (p) => <Badge status={p.status} /> },
            ]} />
            <Pagination page={q.data!.page} totalPages={q.data!.totalPages} total={q.data!.total} onPage={(p) => setF({ page: String(p) } as never)} />
          </>
        )}
      </Card>
    </>
  );
}
